import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import { validateValue, validateFields } from '../../../utilities/validation/validator';
import { validationRules } from '../../../utilities/validation/validationVariables';
import { showAppLoader, hideAppLoader } from '../../Shared/Loader/LoaderSlice';
import ProviderManager from '../../../provider/providerManager';
import { getRecaptchaToken } from '../../../utilities/recaptcha/recaptcha';
import asyncActionStatus from '../../../constans/asyncActionStatus';
import extendedLeadAppearanceMode from '../../../constans/extendedLeadAppearanceMode';
import { sessionGetAndClear } from '../../../utilities/leadStorageAPI/sessionStorageAPI';

const initialState = {
  contentData: {},
  showError: false,
  showSuccess: false,
  showPopup: true,
  form: {
    errors: {},
    rules: {},
    valid: '',
  },
  isAgreementsCheckBoxActive: false
};

const createLead = createAsyncThunk('extendedLeadBlock/createLead', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const blockState = thunkAPI.getState().extendedLeadBlock;
    const isAgreementsCheckBoxActive = blockState.isAgreementsCheckBoxActive;

    const blockId = blockState?.contentData?.blockId;
    const {
      firstName,
      lastName,
      identity,
      payment,
      phoneNumber,
      additionalPhone,
      emailAddress,
      companyName,
      content,
      generalField,
      terms,
      subjectIndex,
    } = blockState?.form;

    let generalFieldData = generalField;

    const uploadFiles = blockState?.form?.uploadFiles?.length > 0 ? blockState.form.uploadFiles.map((x) => x.storageName) : [];

    let clonedForm = _.cloneDeep(blockState?.form);
    const validatedForm = validateFields(clonedForm);
    if (validatedForm.valid === false) {
      return {
        isValid: false,
        form: validatedForm,
      };
    }

    if (blockState.additionalParams?.from == 'fiberNewSale') {
      thunkAPI.dispatch(
        reportAnalytics({
          eventCategory: 'fiber_newsale_process',
          eventAction: 'click_17',
          eventLabel: blockState.additionalParams?.productName,
        })
      );
    }

    if (blockState.additionalParams?.from?.includes('fiber')) {
      const address = blockState.additionalParams?.address;
      generalFieldData = address.cityName + ' ' + address.streetName + ' ' + address.home + ' ' + address.zipCode;
    }

    const subjectObj = blockState.contentData.subjectList[subjectIndex - 1];
    const subject = subjectObj.subjectCode;

    const recaptchaToken = await getRecaptchaToken('extendedLead');

    const result = await ProviderManager.getProviderData('sendExtendedLead', [
      recaptchaToken,
      blockId,
      firstName,
      lastName,
      identity,
      payment,
      phoneNumber,
      additionalPhone,
      emailAddress,
      companyName,
      content,
      generalFieldData,
      terms,
      subject,
      uploadFiles,
      isAgreementsCheckBoxActive,
      data?.utmParams
    ]);

    if (result?.Body?.isSuccess) {
      if (blockState.additionalParams?.from == 'fiberNewSale') {
        thunkAPI.dispatch(reportAnalytics({ eventCategory: 'fiber_newsale_process', eventAction: 'visibility_14', eventLabel: 'screen_2' }));
        thunkAPI.dispatch(
          report({
            type: 'addressNotConnectedToFiber',
            ticketNumber: result?.Body?.ticketNumber,
            form: blockState?.form,
            additionalParams: blockState.additionalParams,
          })
        );
      }
    }

    return {
      isSuccess: result?.Body?.isSuccess,
      ticketNumber: result?.Body?.ticketNumber,
      sla: result?.Body?.sla,
      collaborationUpdateSuccess : result?.Body?.collaborationUpdateSuccess,
      returnCodeMessage : result?.Body?.returnCodeMessage,
    };
  } catch (error) {
    return {
      isSuccess: false,
    };
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});


const openOpportunity = createAsyncThunk('extendedLeadBlock/openOpportunity', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());

    const state = thunkAPI.getState().extendedLeadBlock;

    let clonedForm = _.cloneDeep(state?.form);
    const validatedForm = validateFields(clonedForm);
    if (validatedForm.valid === false) {
      return {
        isValid: false,
        form: validatedForm,
      };
    }

    const params = {
      firstName: state.form.firstName,
      lastName: state.form.lastName,
      phoneNumber: state.form.phoneNumber,
      email: state.form.emailAddress,
      content: state.form.content,
      isAgreementsCheckBoxActive : state.isAgreementsCheckBoxActive
    };

    const result = await ProviderManager.getProviderData('openOpportunity', params);

    return {
      isSuccess: result?.Body?.isSuccess,
      ticketNumber: result?.Body?.ticketNumber,
      sla: result?.Body?.sla,
      collaborationUpdateSuccess : result?.Body?.collaborationUpdateSuccess,
      returnCodeMessage : result?.Body?.returnCodeMessage,
    };
  } catch (error) {
    return {
      isSuccess: false,
    };
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

const leadBlockSlice = createSlice({
  name: 'extendedLeadBlock',
  initialState,
  reducers: {
    setInitialContent: {
      reducer(state, action) {
        state.contentData = action.payload.contentData;
        state.form = {
          ...state.form,
          ...action.payload.form,
        };

        if (action.payload.additionalParams) {
          state.additionalParams = action.payload.additionalParams;
        }
      },
      prepare(payload) {
        const contentData = {
          blockId: payload.contentLink.id,
          appearanceMode: extendedLeadAppearanceMode[payload.eExtendedLeadBlockAppearanceMode.value],
          title: payload.title.value,
          subTitle: payload.subTitle.value,
          topText: payload.topText.value,
          bottomText: payload.bottomText.value,
          successTitle: payload.successTitle.value,
          successSubTitle: payload.successSubTitle.value,
          failureText: payload.failureText.value,
          leadSendButton: payload.leadSendButton.value,
          firstNameLabel: payload.firstNameLabel.value,
          isShowFirstName: !!payload.isShowFirstName?.value,
          isFirstNameRequired: !!payload.isFirstNameRequired?.value,
          lastNameLabel: payload.lastNameLabel.value,
          isShowLastName: !!payload.isShowLastName?.value,
          isLastNameRequired: !!payload.isLastNameRequired?.value,
          identityLabel: payload.identityLabel.value,
          isShowIdentity: !!payload?.isShowIdentity.value,
          isIdentityRequired: !!payload.isIdentityRequired?.value,
          paymentLabel: payload.paymentLabel.value,
          isShowPayment: !!payload.isShowPayment?.value,
          isPaymentRequired: !!payload.isPaymentRequired?.value,
          phoneLabel: payload.phoneLabel.value,
          isShowPhone: !!payload.isShowPhone?.value,
          isPhoneRequired: !!payload.isPhoneRequired?.value,
          additionalPhoneLabel: payload.additionalPhoneLabel.value,
          isShowAdditionalPhone: !!payload.isShowAdditionalPhone?.value,
          isAdditionalPhoneRequired: !!payload.isAdditionalPhoneRequired?.value,
          emailLabel: payload.emailLabel.value,
          isShowEmail: !!payload.isShowEmail?.value,
          isEmailRequired: !!payload.isEmailRequired?.value,
          companyNameLabel: payload.companyNameLabel.value,
          isShowCompanyName: payload.isShowCompanyName.value,
          isCompanyNameRequired: !!payload.isCompanyNameRequired?.value,
          contentLabel: payload.contentLabel.value,
          isShowContent: !!payload.isShowContent?.value,
          isContentRequired: !!payload.isContentRequired?.value,
          generalFieldLabel: payload.generalFieldLabel.value,
          isShowGeneralField: !!payload.isShowGeneralField?.value,
          isGeneralFieldRequired: !!payload.isGeneralFieldRequired?.value,
          termsText: payload.termsText.value,
          isShowTerms: !!payload.isShowTerms?.value,
          isTermsRequired: !!payload.isTermsRequired?.value,
          subjectListLabel: payload.subjectListLabel.value,
          subjectList: payload.subjectList.expandedValue?.map((x) => ({
            subjectName: x.subjectName.value,
            subjectCode: x.subjectCode.value,
            isOpenOpportunity: !!x.isOpenOpportunity.value,
          })),
          isShowUploadFiles: !!payload.isShowUploadFiles?.value,
          maxFilesToUpload: payload.maxFilesToUpload.value,
          uploadFilesExtensions: payload.uploadFilesExtensions.value,
          uploadFilesExplanationText: payload.uploadFilesExplanationText.value,
        };

        let form = {
          rules: {},
        };

        form.rules['subjectIndex'] = validationRules.selectIndexNonZero;
        form['subjectIndex'] = '';

        if (contentData.isShowFirstName) {
          form['firstName'] = '';
          if (contentData.isFirstNameRequired) {
            form.rules['firstName'] = validationRules.onlyRequired;
          }
        }
        if (contentData.isShowLastName) {
          form['lastName'] = '';
          if (contentData.isLastNameRequired) {
            form.rules['lastName'] = validationRules.onlyRequired;
          }
        }
        if (contentData.isShowIdentity) {
          form['identity'] = '';
          if (contentData.isIdentityRequired) {
            form.rules['identity'] = validationRules.id;
          }
        }
        if (contentData.isShowPayment) {
          form['payment'] = '';
          if (contentData.isPaymentRequired) {
            form.rules['payment'] = validationRules.paymentLast4Digits;
          }
        }
        if (contentData.isShowPhone) {
          form['phoneNumber'] = '';
          if (contentData.isPhoneRequired) {
            form.rules['phoneNumber'] = validationRules.phone;
          }
        }
        if (contentData.isShowAdditionalPhone) {
          form['additionalPhone'] = '';
          if (contentData.isAdditionalPhoneRequired) {
            form.rules['additionalPhone'] = validationRules.phone;
          }
        }
        if (contentData.isShowEmail) {
          form['emailAddress'] = '';
          if (contentData.isEmailRequired) {
            form.rules['emailAddress'] = validationRules.email;
          }
        }
        if (contentData.isShowCompanyName) {
          form['companyName'] = '';
          if (contentData.isCompanyNameRequired) {
            form.rules['companyName'] = validationRules.onlyRequired;
          }
        }
        if (contentData.isShowContent) {
          form['content'] = '';
          if (contentData.isContentRequired) {
            form.rules['content'] = validationRules.onlyRequired;
          }
        }
        if (contentData.isShowGeneralField) {
          form['generalField'] = '';
          if (contentData.isGeneralFieldRequired) {
            form.rules['generalField'] = validationRules.onlyRequired;
          }
        }
        if (contentData.isShowTerms) {
          form['terms'] = false;
          if (contentData.isTermsRequired) {
            form.rules['terms'] = validationRules.checkboxRequired;
          }
        }
        if (contentData.isShowUploadFiles) {
          form['uploadFiles'] = [];
        }

        const additionalParams = sessionGetAndClear();

        return {
          payload: {
            contentData,
            form,
            additionalParams,
          },
        };
      },
    },
    setShowPopup: (state, action) => {
      state.showPopup = action.payload;
    },
    setFormField: {
      reducer(state, action) {
        state.form[action.payload.name] = action.payload.value;
        state.form.errors[action.payload.name] = action.payload.error;
        state.form.valid = action.payload.valid;
      },
      prepare(payload) {
        const value = payload.value;
        const name = payload.name;
        const error = validateValue(payload.rule, payload.name, payload.value);
        const valid = !error;

        return {
          payload: {
            name,
            value,
            error,
            valid,
          },
        };
      },
    },
    addFormFile: (state, action) => {
      state.form['uploadFiles'].push(action.payload);
    },
    removeFormFile: (state, action) => {
      const filteredUploadFiles = state.form['uploadFiles'].filter((x) => x.localName != action.payload);
      state.form['uploadFiles'] = filteredUploadFiles;
    },
    reportAnalytics: (state, action) => { },
    report: (state, action) => { },
    resetBlock: (state) => initialState,
    setAgreementsCheckBox: (state, action) => {
      state.isAgreementsCheckBoxActive = action.payload;
    },
  },
  extraReducers: {
    [createLead.fulfilled]: (state, action) => {
      if (action.payload.isValid === false) {
        state.form = action.payload.form;
        return;
      }
      if (action.payload.isSuccess && action.payload.ticketNumber) {
        state.createLeadStatus = asyncActionStatus.SUCCESS;
        state.showSuccess = true;
        state.showError = false;
        state.ticketNumber = action.payload.ticketNumber;
        state.sla = action.payload.sla;
      } else {
        state.createLeadStatus = asyncActionStatus.FAILURE;
        state.showError = true;
        state.showSuccess = false;
      }
    },
    [openOpportunity.fulfilled]: (state, action) => {
      if (action.payload.isValid === false) {
        state.form = action.payload.form;
        return;
      }
      if (action.payload.isSuccess) {
        state.createLeadStatus = asyncActionStatus.SUCCESS;
        state.showSuccess = true;
        state.showError = false;
        state.sla = action.payload?.sla;
      } else {
        state.createLeadStatus = asyncActionStatus.FAILURE;
        state.showError = true;
        state.showSuccess = false;
      }
    },
  },

});


export const {setShowPopup, setInitialContent, setFormField, addFormFile, removeFormFile, reportAnalytics, report, resetBlock, setAgreementsCheckBox } = leadBlockSlice.actions;

export { createLead, openOpportunity };

export default leadBlockSlice.reducer;
